import { motion, useInView } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Home, Shield, Award } from "lucide-react";
import { useRef, useEffect, useState } from "react";

// Animated counter hook
const useCounter = (end: number, duration: number = 2000, startOnView: boolean = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(!startOnView);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (startOnView && isInView) {
      setHasStarted(true);
    }
  }, [isInView, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, ref };
};

const StatsBar = () => {
  const { t: tCommon } = useTranslation('common');

  const roofCounter = useCounter(7000, 2000);
  const yearsCounter = useCounter(25, 1500);

  return (
    <div className="relative z-20 -mt-24 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <motion.div
        className="stats-bar rounded-3xl shadow-stats p-8 sm:p-12 border border-slate-200/50 dark:border-slate-700/50"
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-3 md:gap-12 divide-y sm:divide-y-0 sm:divide-x divide-slate-100 dark:divide-slate-700">
          {/* Roofs Completed */}
          <motion.div
            ref={roofCounter.ref}
            className="flex items-center gap-6 px-4 pt-4 sm:pt-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="p-4 bg-primary/10 rounded-2xl text-primary">
              <Home className="w-8 h-8" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-4xl sm:text-5xl font-heading font-black tracking-tighter text-secondary dark:text-white mb-1">
                {roofCounter.count.toLocaleString()}+
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-widest">
                {tCommon('trustBadges.roofsCompletedShort')}
              </p>
            </div>
          </motion.div>

          {/* Years Experience */}
          <motion.div
            ref={yearsCounter.ref}
            className="flex items-center gap-6 px-4 pt-4 sm:pt-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="p-4 bg-primary/10 rounded-2xl text-primary">
              <Shield className="w-8 h-8" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-4xl sm:text-5xl font-heading font-black tracking-tighter text-secondary dark:text-white mb-1">
                {yearsCounter.count}+
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-widest">
                {tCommon('trustBadges.yearsExperience')}
              </p>
            </div>
          </motion.div>

          {/* Top Rated */}
          <motion.div
            className="flex items-center gap-6 px-4 pt-4 sm:pt-0"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <div className="p-4 bg-primary/10 rounded-2xl text-primary">
              <Award className="w-8 h-8" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-4xl sm:text-5xl font-heading font-black tracking-tighter text-secondary dark:text-white mb-1">
                {tCommon('trustBadges.topRatedValue')}
              </p>
              <p className="text-slate-500 dark:text-slate-400 text-xs font-bold uppercase tracking-widest">
                {tCommon('trustBadges.topRatedLabel')}
              </p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

export default StatsBar;
